import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./providers/AuthContext";
import { AudioProvider } from "./providers/AudioProvider";
import { SettingsProvider } from "./providers/SettingsContext";
import { MessagesProvider } from "./providers/MessagesContext";
import LoginPage from "./pages/LoginPage";
import ChatPage from "./pages/ChatPage";
import DebuggerPage from "./pages/DebuggerPage";
import LoadingScreen from "./components/LoadingScreen";
import ProtectedRoute from "./components/routes/ProtectedRoute";
import RoleProtectedRoute from "./components/routes/RoleProtectedRoute";
import Footer from "./components/Footer";

import { initViewportHeight } from "./utils/viewportUtils";

const App: React.FC = () => {
  const { isAuthenticated, isInitializing } = useAuth();
  const [updateAvailable, setUpdateAvailable] = React.useState(false);
  const [backendVersion, setBackendVersion] = React.useState<string | null>(
    null
  );

  // Initialize viewport height handling for mobile keyboards
  React.useEffect(() => {
    const cleanup = initViewportHeight();
    return cleanup;
  }, []);

  // Check for service worker updates
  React.useEffect(() => {
    const handleUpdate = () => {
      setUpdateAvailable(true);
    };

    // Listen for both the custom event and the original event
    window.addEventListener("sw-update", handleUpdate);

    // Also check if there's already an update available
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        if (reg.waiting) {
          setUpdateAvailable(true);
        }
      });
    }

    return () => window.removeEventListener("sw-update", handleUpdate);
  }, []);

  const reload = () => {
    // Force reload and clear cache for Safari
    if (
      navigator.userAgent.includes("Safari") &&
      !navigator.userAgent.includes("Chrome")
    ) {
      // Safari-specific reload
      window.location.reload();
    } else {
      window.location.reload();
    }
  };

  const handleBackendVersionFetched = React.useCallback((version: string) => {
    setBackendVersion(version);
  }, []);

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <LoadingScreen>
        <div></div>
      </LoadingScreen>
    );
  }

  return (
    <SettingsProvider>
      <MessagesProvider>
        <AudioProvider>
          <div className="flex flex-col bg-[#e3f2fd] min-h-screen">
            <div className="flex-1">
              <Routes>
                <Route
                  // route is 'aveta.fyi/t?t=' followed by args
                  path="/t"
                  element={
                    isAuthenticated ? (
                      <Navigate to="/" replace />
                    ) : (
                      <LoginPage />
                    )
                  }
                />
                <Route
                  path="/login"
                  element={
                    isAuthenticated ? (
                      <Navigate to="/" replace />
                    ) : (
                      <LoginPage />
                    )
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ChatPage
                        updateAvailable={updateAvailable}
                        onUpdateAction={reload}
                        onBackendVersionFetched={handleBackendVersionFetched}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/debugger"
                  element={
                    <RoleProtectedRoute requiredRole="admin">
                      <DebuggerPage
                        updateAvailable={updateAvailable}
                        onUpdateAction={reload}
                      />
                    </RoleProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
            <Footer backendVersion={backendVersion} />
          </div>
        </AudioProvider>
      </MessagesProvider>
    </SettingsProvider>
  );
};

export default App;
